import {
  REFERRAL_EXAMPLE_MAX_BYTES,
  REFERRAL_EXAMPLE_MEDIA_TYPES,
  type ReferralExample,
} from "@rosc/contracts";
import { ClipboardPaste, FileUp, Loader2, Lock, Upload } from "lucide-react";
import type { JSX } from "react";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { V3Button } from "@/components/ui/v3/button";
import { V3Chip } from "@/components/ui/v3/chip";
import { docxText } from "@/lib/referral-templates/docx";
import {
  extractReferralTemplate,
  fileBase64,
  type ReferralExtraction,
} from "@/lib/referral-templates/queries";
import { cn } from "@/lib/utils";
import { ReferralSheet } from "./sheet";
import { FIELD_HINT, FIELD_LABEL, FIELD_TEXTAREA } from "./styles";

const DOCX =
  "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
const ACCEPT = [...REFERRAL_EXAMPLE_MEDIA_TYPES, DOCX, ".docx"].join(",");

type Mode = "file" | "paste";

/** The example as the API takes it: Word files become text in the browser. */
export const exampleFromFile = async (file: File): Promise<ReferralExample> => {
  if (file.size > REFERRAL_EXAMPLE_MAX_BYTES) throw Error("too-large");
  if (file.type === DOCX || /\.docx$/i.test(file.name)) {
    const text = await docxText(await file.arrayBuffer());
    if (text.length < 40) throw Error("unreadable");
    return { kind: "text", text: text.slice(0, 40000) };
  }
  const mediaType = REFERRAL_EXAMPLE_MEDIA_TYPES.find(
    (type) => type === file.type,
  );
  if (!mediaType) throw Error("unsupported");
  return { kind: "file", mediaType, data: await fileBase64(file) };
};

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onExtracted: (extraction: ReferralExtraction) => void;
}

// "From an example letter": upload (PDF, Word, photo) or paste one of your
// letters; the AI keeps its layout and tone and replaces patient details with
// placeholders. Nothing is saved until the template is reviewed.
export const ReferralFromExampleDialog = ({
  open,
  onOpenChange,
  onExtracted,
}: Props): JSX.Element => {
  const { t } = useTranslation();
  const [mode, setMode] = useState<Mode>("file");
  const [file, setFile] = useState<File | null>(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abort = useRef<AbortController | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const textId = useId();
  const privacyId = useId();

  useEffect(() => {
    if (open) {
      setFile(null);
      setText("");
      setError(null);
      setBusy(false);
    } else abort.current?.abort();
  }, [open]);

  const ready = mode === "file" ? !!file : text.trim().length >= 40;
  const run = async (): Promise<void> => {
    if (!ready || busy) return;
    setBusy(true);
    setError(null);
    const controller = new AbortController();
    abort.current = controller;
    try {
      const example: ReferralExample =
        mode === "file" && file
          ? await exampleFromFile(file)
          : { kind: "text", text: text.trim() };
      const extraction = await extractReferralTemplate(
        example,
        controller.signal,
      );
      if (controller.signal.aborted) return;
      onExtracted(extraction);
    } catch (caught) {
      if (controller.signal.aborted) return;
      const reason = caught instanceof Error ? caught.message : "";
      setError(
        reason === "too-large"
          ? t("referral.example.errors.tooLarge")
          : reason === "unsupported"
            ? t("referral.example.errors.unsupported")
            : reason === "unreadable" ||
                (caught as { status?: number }).status === 400
              ? t("referral.example.errors.unreadable")
              : t("referral.example.errors.failed"),
      );
    } finally {
      if (abort.current === controller) setBusy(false);
    }
  };

  return (
    <ReferralSheet
      description={t("referral.example.description")}
      open={open}
      testId="referral-from-example"
      title={t("referral.example.title")}
      onOpenChange={onOpenChange}
      onSubmit={() => {
        void run();
      }}
      footer={
        <>
          <V3Button
            className="max-sm:h-12"
            variant="secondary"
            onClick={() => {
              onOpenChange(false);
            }}
          >
            {t("common.cancel")}
          </V3Button>
          <V3Button
            className="max-sm:h-12"
            data-testid="referral-example-submit"
            disabled={!ready || busy}
            type="submit"
            variant="primary"
          >
            {busy && <Loader2 aria-hidden className="animate-spin" />}
            {busy
              ? t("referral.example.reading")
              : t("referral.example.create")}
          </V3Button>
        </>
      }
    >
      <div
        className="flex gap-3 rounded-xl bg-v3-surface-2 px-3 py-3 text-[13px] leading-5 text-v3-text-2 shadow-v3-hairline"
        id={privacyId}
      >
        <Lock aria-hidden className="mt-0.5 size-4 shrink-0 text-v3-text" />
        <p>
          <span className="font-semibold text-v3-text">
            {t("referral.example.privacyTitle")}
          </span>{" "}
          {t("referral.example.privacy")}
        </p>
      </div>
      <fieldset className="flex gap-2">
        <legend className="sr-only">{t("referral.example.modeLabel")}</legend>
        <V3Chip
          aria-pressed={mode === "file"}
          className="flex-1 justify-center lg:h-9"
          onClick={() => {
            setMode("file");
          }}
        >
          <FileUp aria-hidden className="size-4" />
          {t("referral.example.upload")}
        </V3Chip>
        <V3Chip
          aria-pressed={mode === "paste"}
          className="flex-1 justify-center lg:h-9"
          data-testid="referral-example-paste-mode"
          onClick={() => {
            setMode("paste");
          }}
        >
          <ClipboardPaste aria-hidden className="size-4" />
          {t("referral.example.paste")}
        </V3Chip>
      </fieldset>
      {mode === "file" ? (
        <div className="space-y-1.5">
          <input
            ref={inputRef}
            accept={ACCEPT}
            className="sr-only"
            data-testid="referral-example-file"
            tabIndex={-1}
            type="file"
            onChange={(event) => {
              setFile(event.target.files?.[0] ?? null);
              setError(null);
            }}
          />
          <button
            className="flex min-h-[132px] w-full flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-v3-border-strong bg-v3-panel px-4 py-6 text-center transition-colors hover:bg-v3-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-v3-brand-ring"
            type="button"
            onClick={() => inputRef.current?.click()}
          >
            <Upload aria-hidden className="size-5 text-v3-text-2" />
            <span className="text-[14px] font-semibold text-v3-text">
              {file ? file.name : t("referral.example.choose")}
            </span>
            <span className={FIELD_HINT}>
              {file
                ? t("referral.example.chooseAnother")
                : t("referral.example.formats")}
            </span>
          </button>
        </div>
      ) : (
        <div className="space-y-1.5">
          <label className={FIELD_LABEL} htmlFor={textId}>
            {t("referral.example.pasteLabel")}
          </label>
          <textarea
            className={cn(FIELD_TEXTAREA, "min-h-[200px]")}
            data-testid="referral-example-text"
            id={textId}
            maxLength={40000}
            placeholder={t("referral.example.pastePlaceholder")}
            rows={8}
            value={text}
            onChange={(event) => {
              setText(event.target.value);
              setError(null);
            }}
          />
        </div>
      )}
      {error && (
        <p
          className="rounded-xl bg-v3-rec-tint px-3 py-2 text-[13.5px] text-v3-rec-text"
          role="alert"
        >
          {error}
        </p>
      )}
    </ReferralSheet>
  );
};
